import { getDevices, getDrivers, getSummaryReport } from './traccar';
import { idleForDay } from './idleStore';
import { alertsForEntityOnDay } from './alertLog';
import { saveReport, reportExists } from './reportStore';

const knToMph = (kn) => Math.round((kn || 0) * 1.15078);
const mToMi = (m) => +((m || 0) / 1609.344).toFixed(1);

// Generate a daily report per unit for a given calendar day (Date object).
// Attaches each to the driver currently assigned to that unit.
export async function generateDailyReports(dayDate = new Date()) {
  const y = dayDate.getFullYear(), mo = dayDate.getMonth(), da = dayDate.getDate();
  const from = new Date(y, mo, da, 0, 0, 0).toISOString();
  const to = new Date(y, mo, da, 23, 59, 59).toISOString();
  const dayKey = dayDate.toLocaleDateString('en-CA'); // YYYY-MM-DD  (matches alertLog)
  const idleDayLabel = dayDate.toDateString();
  const todayLabel = new Date().toDateString();

  const [devices, drivers] = await Promise.all([getDevices(), getDrivers().catch(() => [])]);
  const results = [];

  for (const d of devices) {
    const driver = drivers.find((dr) => String((dr.attributes || {}).currentUnit) === String(d.id));
    const id = `${dayKey}:${d.id}`;
    if (await reportExists(id)) continue; // don't regenerate

    let summary = null;
    try { summary = await getSummaryReport(d.id, from, to); } catch { /* leave null */ }

    const vAlerts = alertsForEntityOnDay(d.name, dayKey);
    const dAlerts = driver ? alertsForEntityOnDay(driver.name, dayKey) : { count: 0, byType: {} };
    const idleSec = idleForDay(d.id, idleDayLabel, todayLabel);

    // skip completely empty days (no driving, no alerts, no idle)
    const distance = summary ? mToMi(summary.distance) : 0;
    if (!distance && vAlerts.count === 0 && dAlerts.count === 0 && idleSec === 0) continue;

    const report = {
      id, day: dayKey, generatedAt: new Date().toISOString(),
      deviceId: d.id, deviceName: d.name,
      driverId: driver ? driver.id : null, driverName: driver ? driver.name : 'Unassigned',
      metrics: {
        distanceMi: distance,
        avgSpeedMph: summary ? knToMph(summary.averageSpeed) : 0,
        maxSpeedMph: summary ? knToMph(summary.maxSpeed) : 0,
        engineHours: summary && summary.engineHours ? +(summary.engineHours / 3600000).toFixed(1) : 0,
        idleMin: Math.round(idleSec / 60),
        fuelUsed: summary && summary.spentFuel ? +summary.spentFuel.toFixed(1) : null,
        startOdo: summary ? mToMi(summary.startOdometer) : null,
        endOdo: summary ? mToMi(summary.endOdometer) : null,
      },
      alertCount: vAlerts.count + dAlerts.count,
      alertsByType: { ...vAlerts.byType, ...dAlerts.byType },
    };
    await saveReport(report);
    results.push(report);
  }
  return results;
}

const LAST_KEY = 'dd-last-report-day';

// Auto-run: if we've rolled into a new day and haven't reported yesterday, do it.
export async function autoGenerateIfNeeded() {
  const todayKey = new Date().toLocaleDateString('en-CA');
  const last = localStorage.getItem(LAST_KEY);
  if (last === todayKey) return; // already ran today
  // generate for yesterday
  const yesterday = new Date(); yesterday.setDate(yesterday.getDate() - 1);
  try {
    await generateDailyReports(yesterday);
    localStorage.setItem(LAST_KEY, todayKey);
  } catch { /* retry next load */ }
}
